/**
 * Reference implementation of the run-from-here API contract (symmy MR !415).
 * Adopt, gut, or rewrite freely — the contract is the deliverable, the
 * component is a suggestion.
 *
 * Contract (all under /api/v1/flows/, same auth as the rest of the API):
 *   1. GET  analytics/flows/logs/?flow_uuids=<flow>&limit=20&sort_by=start
 *      → { logs: [{ _id, start, status: {name}, trigger_type? }], total_count }
 *      (the run picker; _id is the source_mongo_flow_id)
 *   2. GET  flows/execute-from-node/<flow>/preview/?node_uuid&source_mongo_flow_id
 *      → { input_data: {"<node_uuid>.<field>": value}, unserializable_keys: [] }
 *      (everything the node consumed in that run, keyed exactly as the POST
 *      override accepts; unserializable values are omitted — a run without an
 *      override for those keys still uses the stored originals)
 *   3. POST flows/execute-from-node/<flow>/
 *      { node_uuid, source_mongo_flow_id?, input_data? } → { mongo_flow_id }
 *      Errors: 400 validation / 404 not found / 409 already running /
 *      410 source data expired — message in response.data.error_text.
 *
 * The edit-and-rerun loop: pick a run → Load data → edit one value (or cut a
 * list down to the one broken record) → Run. Re-running a node re-does its
 * writes — side effects are the caller's responsibility.
 */
import { useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Alert, Button, Modal, Select, Space } from 'antd';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useQueryClient } from '@tanstack/react-query';

import { API, QUERY_KEYS } from '@config';
import JsonViewEdit from '@components/fields/JsonViewEdit';
import { useActiveFlowUuid } from '@hooks/core';
import { useTimezoneAwareDateFormatters } from '@hooks/timezone';
import { useMessage, useParam } from '@hooks/utility';
import { useRequest } from '@hooks/useRequest';

import {
  usePreviewNodeInputMutation,
  useRecentFlowRunsQuery,
} from './RunFromHereModal.query';
import styles from './RunFromHereModal.module.scss';

type RunFromHereModalProps = {
  nodeUuid: string;
  nodeTitle?: string;
  open: boolean;
  onClose: () => void;
};

const RunFromHereModal = ({
  nodeUuid,
  nodeTitle,
  open,
  onClose,
}: RunFromHereModalProps) => {
  const { _ } = useLingui();
  const message = useMessage();
  const flowUuid = useActiveFlowUuid();
  const request = useRequest();
  const queryClient = useQueryClient();
  const { dayMonthYearHourMinute } = useTimezoneAwareDateFormatters();

  // The run the user is currently looking at. "Run from here" only renders in
  // the read-only detail view (/flow/:flow/call/:flowCall), so this is almost
  // always set — and it is the run they mean. useParam, not
  // useParamIdOrRedirect: on a route without the param the latter navigates
  // away, and FlowBuilder also renders on the builder page and the dashboard.
  const currentRunId = useParam('flowCall');

  const [selectedRunId, setSelectedRunId] = useState<string | null>(
    currentRunId ?? null,
  );
  const [inputData, setInputData] = useState<Record<string, unknown> | null>(
    null,
  );
  const [executing, setExecuting] = useState(false);

  const {
    data: runs = [],
    isLoading: runsLoading,
    isError: runsError,
  } = useRecentFlowRunsQuery(flowUuid, { enabled: open });
  const previewMutation = usePreviewNodeInputMutation(flowUuid);

  const runOptions = useMemo(() => {
    // One marker for "the run you are looking at", used as a suffix on the
    // dated label and alone on the fallback below. Two separate strings would
    // reach translators as unrelated entries for the same concept.
    const currentRunMarker = _(msg`this run`);
    const options = runs.map((run) => ({
      value: run._id,
      label: `${dayMonthYearHourMinute(run.start)} — ${
        run.status?.name ?? '?'
      } (${run.trigger_type ?? '—'})${
        run._id === currentRunId ? ` — ${currentRunMarker}` : ''
      }`,
    }));
    // The open run can be older than the twenty the picker lists (a run opened
    // from a link or from search), and then it would be missing from the very
    // list it is preselected in. Offer it explicitly rather than silently
    // falling back to "no run selected" — but only once the list has settled,
    // or the preselected label would rewrite itself the moment the runs land.
    // A failed query keeps the fallback: the user can still run from the node.
    if (
      currentRunId &&
      !runsLoading &&
      !runs.some((run) => run._id === currentRunId)
    ) {
      options.unshift({ value: currentRunId, label: currentRunMarker });
    }
    return options;
  }, [runs, runsLoading, currentRunId, dayMonthYearHourMinute, _]);

  const showApiError = (error: unknown, fallback: string) => {
    if (error instanceof AxiosError && error.response?.data?.error_text) {
      message.error(error.response.data.error_text);
    } else {
      message.error(fallback);
    }
  };

  const handleLoadData = () => {
    if (!flowUuid || !selectedRunId) return;
    previewMutation.mutate(
      { nodeUuid, sourceRunId: selectedRunId },
      {
        onSuccess: (data) => {
          setInputData(data.input_data);
          const omitted = data.unserializable_keys.join(', ');
          if (data.unserializable_keys.length > 0) {
            message.warning(
              _(
                msg`Some values can't be edited as JSON and were omitted: ${omitted}`,
              ),
            );
          }
          if (data.missing_references?.length > 0) {
            // Drift: this run predates a flow change, so the node now reads keys
            // the run never produced — the run would fail unless these are supplied.
            const missing = data.missing_references.join(', ');
            message.warning(
              _(
                msg`This run predates a change to the flow; the node now needs keys it didn't produce: ${missing}`,
              ),
            );
          }
        },
        onError: (error) =>
          showApiError(error, _(msg`Failed to load run data`)),
      },
    );
  };

  const handleRun = async () => {
    if (!flowUuid) return;
    setExecuting(true);
    try {
      const hasOverride = inputData && Object.keys(inputData).length > 0;
      await request(API.FLOWS_EXECUTE_FROM_NODE(flowUuid), {
        method: 'POST',
        data: {
          node_uuid: nodeUuid,
          ...(selectedRunId && { source_mongo_flow_id: selectedRunId }),
          ...(hasOverride && { input_data: inputData }),
        },
      });
      message.success(_(msg`Partial run started`));
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_CALLS] });
      onClose();
    } catch (error) {
      showApiError(error, _(msg`Failed to start the partial run`));
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Modal
      title={
        nodeTitle
          ? _(msg`Run from here — ${nodeTitle}`)
          : _(msg`Run from here`)
      }
      open={open}
      onCancel={onClose}
      okText={_(msg`Run from node`)}
      onOk={handleRun}
      okButtonProps={{
        loading: executing,
        disabled:
          !selectedRunId && !(inputData && Object.keys(inputData).length > 0),
      }}
      width={720}
    >
      <Space direction="vertical" className={styles.fullWidth} size="middle">
        <Alert
          type="warning"
          showIcon
          message={
            <Trans>
              This node and its descendants will execute again — re-running
              re-does their writes.
            </Trans>
          }
        />
        <Select
          className={styles.fullWidth}
          placeholder={_(msg`Pick a source run`)}
          loading={runsLoading}
          // While the runs load there is no option to match the preselected id,
          // and antd renders the bare id in that case. Show the placeholder
          // under the spinner instead; the selection itself is kept.
          value={runsLoading ? null : selectedRunId}
          onChange={setSelectedRunId}
          options={runOptions}
        />
        {runsError && (
          <Alert
            type="error"
            showIcon
            message={<Trans>Failed to load recent runs</Trans>}
          />
        )}
        <Button
          type="link"
          className={styles.loadDataButton}
          loading={previewMutation.isLoading}
          disabled={!selectedRunId}
          onClick={handleLoadData}
        >
          <Trans>Load data from the selected run (edit before running)</Trans>
        </Button>
        <JsonViewEdit
          value={inputData}
          onChange={setInputData}
          label={_(msg`Input data override`)}
          placeholder={_(
            msg`Optional. Keys must be "<node_uuid>.<field>" entries; they win over the source run's data.`,
          )}
        />
      </Space>
    </Modal>
  );
};

export default RunFromHereModal;
